import { API, Auth } from 'aws-amplify'
import { PaymentPlatform, DeviceType } from '../types/donation'

const API_NAME = 'DonationsAPI'

// Helper to get auth headers
const getAuthHeaders = async () => {
  const session = await Auth.currentSession()
  return {
    Authorization: `Bearer ${session.getIdToken().getJwtToken()}`
  }
}

// API types (snake_case)
interface GiftAidDonorTransactionApi {
  id: string
  amount: number
  date: string
  donation_type: string
  platform: PaymentPlatform
  device?: DeviceType
  reference?: string
  gift_aid: boolean
  gift_aid_covered?: boolean
}

interface GiftAidDonorApi {
  key: string
  title?: string
  first_name: string
  last_name: string
  address_line1?: string
  address_line2?: string
  city?: string
  postcode?: string
  email?: string
  consented: boolean
  transaction_count: number
  total_amount: number
  last_generated_at?: string
  last_declaration_id?: string
  identity_keys?: string[]
  merge?: {
    id: string
    labels: string[] | null
    reason?: string
    reason_detail?: string
    created_by?: string
    created_at: string
  }
  transactions: GiftAidDonorTransactionApi[]
}

interface FormsZipResponseApi {
  url: string
  file_name: string
  file_count: number
  missing: string[] | null
}

interface GiftAidDonorsResponseApi {
  donors: GiftAidDonorApi[]
  unmatched_count: number
}

// Frontend types (camelCase)
export interface GiftAidDonorTransaction {
  id: string
  amount: number // pence
  date: string // YYYY-MM-DD
  donationType: string
  platform: PaymentPlatform
  device?: DeviceType
  reference?: string
  giftAid: boolean
  // True when Gift Aid can be claimed: ticked, own declaration, or donor has a multiple-donation declaration
  giftAidCovered: boolean
}

export interface GiftAidDonor {
  key: string
  title: string
  firstName: string
  lastName: string
  addressLine1: string
  addressLine2: string
  city: string
  postcode: string
  email: string
  consented: boolean
  transactionCount: number
  totalAmount: number // pence
  lastGeneratedAt?: string
  lastDeclarationId?: string
  // Matching keys of every identity grouped into this donor, sent back when merging
  identityKeys: string[]
  // Set when an admin merged several donor cards into this one
  merge?: GiftAidDonorMerge
  transactions: GiftAidDonorTransaction[]
}

export interface GiftAidDonorMerge {
  id: string
  labels: string[]
  reason: MergeReason | ''
  reasonDetail: string
  createdBy: string
  createdAt: string
}

export type MergeReason = 'donor_confirmed' | 'same_contact' | 'moved_house' | 'other'

export const MERGE_REASONS: { id: MergeReason; label: string }[] = [
  { id: 'donor_confirmed', label: 'Spoke to the donor and they confirmed' },
  { id: 'same_contact', label: 'Same email or phone number' },
  { id: 'moved_house', label: 'Donor moved house' },
  { id: 'other', label: 'Other' },
]

export interface MergeCard {
  identityKeys: string[]
  label: string
}

export interface MergeIssue {
  code: string
  message: string
}

// What blocks a merge and what the admin must acknowledge, worked out by the API
export interface MergeCheck {
  blocks: MergeIssue[]
  warnings: MergeIssue[]
}

export interface MergeDonorsRequest extends MultiDeclarationRequest {
  donors: MergeCard[]
  reason: MergeReason
  reasonDetail: string
  acknowledgedWarnings: string[]
}

export interface MergeDonorsResult {
  mergeId: string
  url: string
}

export interface GiftAidDonorsResult {
  donors: GiftAidDonor[]
  unmatchedCount: number
}

export interface FormsZipResult {
  url: string
  fileName: string
  fileCount: number
  missing: string[]
}

export interface MultiDeclarationRequest {
  title: string
  firstName: string
  lastName: string
  addressLine1: string
  addressLine2: string
  city: string
  postcode: string
  email: string
  transactionIds: string[]
}

const transformDonor = (d: GiftAidDonorApi): GiftAidDonor => ({
  key: d.key,
  title: d.title || '',
  firstName: d.first_name,
  lastName: d.last_name,
  addressLine1: d.address_line1 || '',
  addressLine2: d.address_line2 || '',
  city: d.city || '',
  postcode: d.postcode || '',
  email: d.email || '',
  consented: d.consented,
  transactionCount: d.transaction_count,
  totalAmount: d.total_amount,
  lastGeneratedAt: d.last_generated_at,
  lastDeclarationId: d.last_declaration_id,
  identityKeys: d.identity_keys || [],
  merge: d.merge
    ? {
        id: d.merge.id,
        labels: d.merge.labels || [],
        reason: (d.merge.reason || '') as MergeReason | '',
        reasonDetail: d.merge.reason_detail || '',
        createdBy: d.merge.created_by || '',
        createdAt: d.merge.created_at,
      }
    : undefined,
  transactions: (d.transactions || []).map(t => ({
    id: t.id,
    amount: t.amount,
    date: t.date,
    donationType: t.donation_type,
    platform: t.platform,
    device: t.device,
    reference: t.reference,
    giftAid: t.gift_aid,
    giftAidCovered: Boolean(t.gift_aid_covered),
  })),
})

const declarationBody = (req: MultiDeclarationRequest) => ({
  donor_title: req.title || undefined,
  donor_first_name: req.firstName,
  donor_last_name: req.lastName,
  donor_address_line1: req.addressLine1,
  donor_address_line2: req.addressLine2 || undefined,
  donor_city: req.city,
  donor_postcode: req.postcode,
  donor_email: req.email || undefined,
  transaction_ids: req.transactionIds,
})

const mergeCardsBody = (cards: MergeCard[]) =>
  cards.map(c => ({ identity_keys: c.identityKeys, label: c.label }))

export const giftAidApi = {
  // List donors from the last 4 years, grouped by person
  async listDonors(): Promise<GiftAidDonorsResult> {
    const headers = await getAuthHeaders()
    const response: GiftAidDonorsResponseApi = await API.get(API_NAME, '/gift-aid/donors', { headers })
    return {
      donors: (response.donors || []).map(transformDonor),
      unmatchedCount: response.unmatched_count || 0,
    }
  },

  // Generate a multiple-donation declaration and return a PDF download URL
  async createMultiDeclaration(req: MultiDeclarationRequest): Promise<string> {
    const headers = await getAuthHeaders()
    const response = await API.post(API_NAME, '/gift-aid/multiple', { headers, body: declarationBody(req) })
    return response.url
  },

  // Merge donor cards into one donor. Generates a new multiple-donation declaration
  // with the chosen details and returns its PDF URL.
  async mergeDonors(req: MergeDonorsRequest): Promise<MergeDonorsResult> {
    const headers = await getAuthHeaders()
    const body = {
      ...declarationBody(req),
      donors: mergeCardsBody(req.donors),
      reason: req.reason,
      reason_detail: req.reasonDetail || undefined,
      acknowledged_warnings: req.acknowledgedWarnings,
    }
    const response = await API.post(API_NAME, '/gift-aid/merges', { headers, body })
    return { mergeId: response.merge.id, url: response.url }
  },

  // Check a merge without saving anything: what blocks it and what needs acknowledging
  async checkMerge(donors: MergeCard[]): Promise<MergeCheck> {
    const headers = await getAuthHeaders()
    const body = { donors: mergeCardsBody(donors), dry_run: true }
    const response = await API.post(API_NAME, '/gift-aid/merges', { headers, body })
    return {
      blocks: response.check?.blocks || [],
      warnings: response.check?.warnings || [],
    }
  },

  // Undo a merge: cancels its declaration and splits the donor cards again
  async unmergeDonors(mergeId: string): Promise<void> {
    const headers = await getAuthHeaders()
    await API.del(API_NAME, `/gift-aid/merges/${mergeId}`, { headers })
  },

  // Get a download URL for an existing multiple-donation declaration (doesn't create a new one)
  async getMultiDeclarationPdfUrl(declarationId: string): Promise<string> {
    const headers = await getAuthHeaders()
    const response = await API.get(API_NAME, `/gift-aid/multiple/${declarationId}/pdf`, { headers })
    return response.url
  },

  // Bundle multiple-donation declaration PDFs into one ZIP and return a download URL
  async createFormsZip(declarationIds: string[], fileName: string): Promise<FormsZipResult> {
    const headers = await getAuthHeaders()
    const body = { declaration_ids: declarationIds, file_name: fileName }
    const response: FormsZipResponseApi = await API.post(API_NAME, '/gift-aid/multiple/zip', { headers, body })
    return {
      url: response.url,
      fileName: response.file_name,
      fileCount: response.file_count,
      missing: response.missing || [],
    }
  },
}
